import React, { useEffect, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { getDisplayableImageUrl } from '../utils/mediaUrl';

type ArticleImageVariant = 'card' | 'related' | 'hero';

const VARIANT_STYLES: Record<ArticleImageVariant, SxProps<Theme>> = {
  card: {
    width: '100%',
    height: 180,
    flexShrink: 0,
  },
  related: {
    width: '100%',
    height: '100%',
  },
  hero: {
    width: '100%',
    maxHeight: 420,
    height: { xs: 220, sm: 320, md: 420 },
    mb: 4,
    borderRadius: 3,
  },
};

interface ArticleImageProps {
  imageUrl?: string | null;
  alt: string;
  variant?: ArticleImageVariant;
  sx?: SxProps<Theme>;
}

/**
 * Renders article images without load-gated opacity/skeleton overlays.
 * Those caused random blank cards: cached images often skip onLoad during SPA nav,
 * leaving an opaque skeleton on top forever until a hard refresh.
 */
const ArticleImage: React.FC<ArticleImageProps> = ({
  imageUrl,
  alt,
  variant = 'card',
  sx,
}) => {
  const [failed, setFailed] = useState(false);
  const displayUrl = getDisplayableImageUrl(imageUrl);

  useEffect(() => {
    setFailed(false);
  }, [displayUrl]);

  if (!displayUrl || failed) {
    return (
      <Box
        sx={[
          {
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(17, 94, 89, 0.06) 0%, rgba(53, 188, 179, 0.06) 100%)',
            borderBottom: '1px solid rgba(53, 188, 179, 0.06)',
          },
          VARIANT_STYLES[variant],
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <ArticleIcon sx={{ fontSize: 44, color: 'rgba(42, 168, 153, 0.35)' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          display: 'block',
          bgcolor: 'rgba(53, 188, 179, 0.06)',
        },
        VARIANT_STYLES[variant],
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        component="img"
        key={displayUrl}
        src={displayUrl}
        alt={alt}
        loading={variant === 'hero' ? 'eager' : 'lazy'}
        decoding="async"
        onError={() => setFailed(true)}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
};

export default ArticleImage;
